import dayjs from "dayjs";

import lodashIsEqual from "lodash/isEqual";
import lodashGet from "lodash/get";
import lodashHas from "lodash/has";

import { datadogRum } from "@datadog/browser-rum";

import { Logger } from "@/common/log.utilities.js";
import { isFeatureUnlocked } from "@/common/featureLock.js";

const logger = new Logger({
  modulePrefix: "USER SERVICE",
  file: "user.service.js",
});

const EMAIL_IGNORE_LIST = ["@geospark.io", "@seerist.com"];

const DEFAULT_LANDING_PAGE = "latest";

import { SAVE_PREFS_INTERVAL_MINUTES } from "@/common/constants/home";
import {
  ENDPOINT_USER_GROUP_MEMBERS,
  ENDPOINT_USER_GROUPS,
  ENDPOINT_USER_ACCEPT_WELCOME,
  ENDPOINT_USER_PROFILE,
} from "@/common/constants/api";
import {
  getUserProfile,
  setUserProfile,
  getUserGroups,
  getOrganizationGroup,
} from "@/common/storage";
import { httpGet, httpPost, httpPatch } from "@/services/api.service";
import {
  getUserProfileById,
  updatePreferences,
} from "@/common/constants/graphql/user";

export { _getProfile as getProfile };
export { _getPreferences as getPreferences };
export { _savePreferences as savePreferences };
export { _setupSavePreferences as setupSavePreferences };
export { _getGroupMembers as getGroupMembers };
export { _getGroups as getGroups };
export { _getGroupsInProfileMetadata as getGroupsInProfileMetadata };
export { _hasGroupByName as hasGroupByName };
export { _isAdmin as isAdmin };
export { _hasTwitterAccess as hasTwitterAccess };
export { _hasFirebaseAccess as hasFirebaseAccess };
export { _hasWatchPlusAccess as hasWatchPlusAccess };
export { _hasWatchOnlyAccess as hasWatchOnlyAccess };
export { _hasSearchOnlyAccess as hasSearchOnlyAccess };
export { _hasSourceLinkPopupAccess as hasSourceLinkPopupAccess };
export { _hasArticleHideImageAccess as hasArticleHideImageAccess };
export { _hasMassCommAdminAccess as hasMassCommAdminAccess };
export { _hasPlusCyberAccess as hasPlusCyberAccess };
export { _hasEssentialsAccess as hasEssentialsAccess };
export { _hasInternationalSOSAccess as hasInternationalSOSAccess };
export { _hasFiriAppAccess as hasFiriAppAccess };
export { _isInternalUser as isInternalUser };
export { _hasGrdAccess as hasGrdAccess };
export { _hasMobileOnlyAccess as hasMobileOnlyAccess };
export { _isInIgnoreUserList as isInIgnoreUserList };
export { _isWhiteLabelUser as isWhiteLabelUser };
export { _acceptWelcomeMessage as acceptWelcomeMessage };
export { _getPreferredLandingPage as getPreferredLandingPage };
export { _memberHasAccessGroupByName as memberHasAccessGroupByName };
export { _hasAssetsMgmtAdminAccess as hasAssetsMgmtAdminAccess };
export { _getUserType as getUserType };
export { _hasCollaborationAccess as hasCollaborationAccess };
export { _hasCopyAccess as hasCopyAccess };
export { _hasPulseAccess as hasPulseAccess };
export { _hasMaritimeAccess as hasMaritimeAccess };
export { _hasCitySummaryAccess as hasCitySummaryAccess };
export { _datadogSetUserMetadata as datadogSetUserMetadata };
export { _hasMyIncidentsEditor as hasMyIncidentsEditorAccess };
export { _hasBreakingEventsAccess as hasBreakingEventsAccess };
export { _hasMyIncidentsAccess as hasMyIncidentsAccess };

let lastAuth0CallDate = dayjs().subtract(1, "day");

const _getProfile = async () => {
  try {
    return await httpPost({
      url: ENDPOINT_USER_PROFILE,
      data: {
        query: getUserProfileById,
        authMode: "OPENID_CONNECT",
      },
    });
  } catch (error) {
    console.error("Unable to retrieve user profile", error);
    return false;
  }
};

const _getPreferences = () => {
  let userProfile = getUserProfile();
  if (userProfile) {
    _setDefaultProfileProperties(userProfile);
    return JSON.parse(JSON.stringify(userProfile.preferences));
  }
  return false;
};

const _getPreferredLandingPage = () => {
  let userProfile = getUserProfile();
  if (
    userProfile &&
    userProfile.preferences?.home?.landingPage &&
    typeof userProfile.preferences.home.landingPage === "string"
  ) {
    return userProfile.preferences.home.landingPage;
  }
  return getDefaultLandingPage();
};

const getDefaultLandingPage = function () {
  if (_hasGrdAccess()) {
    return "grd"; //Todo: if list gets longer, move this to constants file including DEFAULT_LANDING_PAGE.
  }
  return DEFAULT_LANDING_PAGE;
};

const _setupSavePreferences = (preferences) => {
  const CURRENT_PREFS = _getPreferences();
  let userProfile = getUserProfile();
  if (userProfile) {
    _setDefaultProfileProperties(userProfile);
    _cleanUpV1Preferences(userProfile);

    if (
      CURRENT_PREFS &&
      preferences &&
      !lodashIsEqual(CURRENT_PREFS.v2, preferences)
    ) {
      _cleanUpUnusedPreferences(preferences);
      userProfile.preferences = JSON.parse(JSON.stringify(preferences));
    }

    setUserProfile(JSON.stringify(userProfile));
    return userProfile.preferences;
  }
  return null;
};

const _savePreferences = async (preferences, force) => {
  const PREFS = _setupSavePreferences(preferences);
  if (PREFS) {
    const DIFF_MINUTES = dayjs().diff(lastAuth0CallDate, "minutes");
    if (force || DIFF_MINUTES > SAVE_PREFS_INTERVAL_MINUTES) {
      lastAuth0CallDate = dayjs();
      return await httpPost({
        url: ENDPOINT_USER_PROFILE,
        data: {
          query: updatePreferences,
          variables: {
            updatepreferencesinput: {
              preferences: JSON.stringify(PREFS),
            },
          },
          authMode: "OPENID_CONNECT",
        },
      });
    }
  }
  return Promise.resolve(false);
};

const _setDefaultProfileProperties = (userProfile) => {
  userProfile.preferences = userProfile.preferences || {};
  userProfile.preferences.updated = new Date();
};

const _cleanUpUnusedPreferences = (preferences) => {
  // V2 stuff that's not used anymore
  if (
    lodashGet(preferences, "home") &&
    lodashHas(preferences.home, "infoViewer")
  ) {
    delete preferences.home["infoViewer"];
  }
  if (
    lodashGet(preferences, "home") &&
    lodashHas(preferences.home, "liveNews")
  ) {
    delete preferences.home["liveNews"];
  }
  if (lodashGet(preferences, "places")) {
    preferences.places.forEach((place) => {
      if (place["data"]) {
        delete place["data"];
      }
      if (place["color"]) {
        delete place["color"];
      }
    });
  }
  if (
    lodashGet(preferences, "alerts.content") &&
    lodashHas(preferences.alerts.content, "alertFilter") &&
    Array.isArray(preferences.alerts.content.alertFilter)
  ) {
    let cleanAlerts = [];
    preferences.alerts.content.alertFilter.forEach((alert) => {
      cleanAlerts.push({
        id: alert.id,
        name: alert.name,
      });
    });
    preferences.alerts.content.alertFilter = cleanAlerts;
  }
};

const _cleanUpV1Preferences = (userProfile) => {
  // V1 stuff
  if (
    lodashGet(userProfile, "user_metadata.preferences") &&
    lodashHas(userProfile.user_metadata.preferences, "alertedContent")
  ) {
    delete userProfile.user_metadata.preferences["alertedContent"];
  }
  if (
    lodashGet(userProfile, "user_metadata.preferences") &&
    lodashHas(userProfile.user_metadata.preferences, "dashboards")
  ) {
    delete userProfile.user_metadata.preferences["dashboards"];
  }
  if (
    lodashGet(userProfile, "user_metadata.preferences") &&
    lodashHas(userProfile.user_metadata.preferences, "hyperReport")
  ) {
    delete userProfile.user_metadata.preferences["hyperReport"];
  }
  if (
    lodashGet(userProfile, "user_metadata.preferences") &&
    lodashHas(userProfile.user_metadata.preferences, "language")
  ) {
    delete userProfile.user_metadata.preferences["language"];
  }
  if (
    lodashGet(userProfile, "user_metadata.preferences") &&
    lodashHas(userProfile.user_metadata.preferences, "tabs")
  ) {
    delete userProfile.user_metadata.preferences["tabs"];
  }
};

const _getGroupMembers = async () => {
  try {
    return await httpGet({ url: ENDPOINT_USER_GROUP_MEMBERS });
  } catch (error) {
    logger.error("FAILED TO GET USER MEMBERS");
    return false;
  }
};

/**
 * @overview Get a user's groups from auth0
 */
const _getGroups = async () => {
  try {
    const groups = await httpGet({
      url: `${ENDPOINT_USER_GROUPS}`,
      data: {},
      noCache: true,
    });
    return JSON.parse(groups).data.data;
  } catch (error) {
    logger.error("FAILED TO GET USER GROUPS");
    return null;
  }
};

const _getGroupsInProfileMetadata = async () => {
  const RESULT = await _getProfile();
  const { data } = JSON.parse(RESULT);
  const groups = data?.user_profile?.userGroups.map((g) => {
    return {
      name: g,
    };
  });
  return groups;
};

/**
 * @overview Check whether a user is part of a given group or not
 */
const _hasGroupByName = (groupName) => {
  const userGroups = getUserGroups();
  let groupCheck;
  if (userGroups) {
    groupCheck = userGroups.filter(
      (userGroupName) => userGroupName.toLowerCase() === groupName.toLowerCase()
    );
  }
  return groupCheck && groupCheck.length > 0;
};

const _isAdmin = () => {
  const userGroups = getUserGroups();
  let groupCheck;
  if (userGroups) {
    groupCheck = userGroups.filter(
      (userGroupName) => userGroupName.toUpperCase() === "ADMIN"
    );
  }
  return groupCheck && groupCheck.length > 0;
};

const _hasAccess = (groupName) => {
  const userGroups = getUserGroups();
  return !!(
    groupName &&
    userGroups?.find((userGroupName) => userGroupName === groupName)
  );
};

const _hasTwitterAccess = () => {
  return _isAdmin() || _hasAccess("Twitter_Access");
};

const _hasFirebaseAccess = () => {
  return _isAdmin() || _hasAccess("Firebase_Disable");
};

const _hasWatchPlusAccess = () => {
  return _hasAccess("WatchPlus_Access");
};

const _hasWatchOnlyAccess = () => {
  return _hasAccess("WatchOnly_Access");
};

const _hasSearchOnlyAccess = () => {
  return _hasAccess("SearchOnly_Access");
};

const _hasSourceLinkPopupAccess = () => {
  return _hasAccess("Source_Link_Popup_Access");
};

const _hasArticleHideImageAccess = () => {
  return _hasAccess("ArticleHideImage_Access");
};

const _hasMassCommAdminAccess = () => {
  return _hasAccess("MassCommsAdmin_Access");
};

const _hasPlusCyberAccess = () => {
  return _hasAccess("Seerist_Cyber_Access");
};

const _hasEssentialsAccess = () => {
  return _hasAccess("Seerist_Essentials_Access");
};

const _hasFiriAppAccess = () => {
  return _hasAccess("FIRI_Access");
};

const _hasInternationalSOSAccess = () => {
  return _hasAccess("InternationalSOS_Access");
};

const _isInternalUser = () => {
  return _hasAccess("User_Geospark");
};

const _hasGrdAccess = () => {
  return _hasAccess("GRD_Access");
};

const _hasMobileOnlyAccess = () => {
  return _hasAccess("MobileOnly_Access");
};

const _hasAssetsMgmtAdminAccess = () => {
  return _hasAccess("AssetsMgmtAdmin_Access");
};

const _hasMaritimeAccess = () => {
  return _hasAccess("Seerist_Maritime_Access");
  // If Seerist_Maritime_Access is removed, please add below to make sure GRD user doesn't have it.
  // return _hasGrdAccess() ? false : true;
};

const _hasCitySummaryAccess = () => {
  return _hasAccess("Seerist_Cities_Access");
};

const _isInIgnoreUserList = () => {
  const USER = getUserProfile();
  let exists = false;
  if (USER && USER.email) {
    EMAIL_IGNORE_LIST.forEach((item) => {
      if (USER.email.indexOf(item) > -1) {
        exists = true;
      }
    });
  }
  return exists;
};

const _isWhiteLabelUser = () => {
  const WHITE_LABEL_GROUPS = ["User_Brosnan_Risk_Consultants"];
  const userGroups = getUserGroups();
  let groupCheck;
  if (userGroups) {
    groupCheck = userGroups.filter(
      (userGroupName) => WHITE_LABEL_GROUPS.indexOf(userGroupName) > -1
    );
  }
  return groupCheck && groupCheck.length > 0;
};

const _acceptWelcomeMessage = async () => {
  try {
    let result = await httpPatch({
      url: ENDPOINT_USER_ACCEPT_WELCOME,
      data: {},
    });
    JSON.parse(result);
    return true;
  } catch (err) {
    console.error("Unable to accept welcome message", error);
    return false;
  }
};

// member specific functions (not currentlly logged in user)
const _memberHasAccessGroupByName = (member, groupName) => {
  const memberAccessGroups = lodashGet(member, "accessGroups");
  let groupCheck;
  if (memberAccessGroups && groupName) {
    groupCheck = memberAccessGroups.filter(
      (el) => el.toLowerCase() === groupName.toLowerCase()
    );
  }
  return groupCheck && groupCheck.length > 0;
};

// Used in handlePageTrackerEvent to get user type (based on groups access) info.
const _getUserType = () => {
  // setup lookup if list of user type needed to track gets long and required to check multiple combinatins.
  return _hasGrdAccess() ? "GRD" : null;
};

const _hasCollaborationAccess = () => {
  return !_hasGrdAccess();
};

const _hasCopyAccess = () => {
  return !_hasGrdAccess();
};

const _hasPulseAccess = () => {
  return !_hasGrdAccess();
};

const _datadogSetUserMetadata = function () {
  const profile = JSON.parse(localStorage.getItem("user_profile")) || {};
  let rumParams = {};
  if (profile.user_id) {
    rumParams.id = profile.user_id;
  }
  if (profile.email) {
    rumParams.email = profile.email;
  }
  let orgGroup = getOrganizationGroup();
  if (orgGroup && orgGroup.name) {
    rumParams.group = orgGroup.name;
  }

  datadogRum.setUser(rumParams);
};

const _hasMyIncidentsEditor = () => {
  const userGroups = getUserGroups() ?? [];
  return userGroups.some(
    (userGroupName) => userGroupName === "MyIncidentsEditor_Access"
  );
};

const _hasBreakingEventsAccess = () => {
  return (
    !_hasGrdAccess() &&
    !_hasEssentialsAccess() &&
    isFeatureUnlocked("breaking-events")
  );
};

const _hasMyIncidentsAccess = () => {
  return isFeatureUnlocked("my-incidents");
};
